<template>
  <div
    :class="
      twJoin(
        props.isCustom &&
          'absolute left-[50%] top-[50%] translate-x-[-50%] xl:translate-y-[-50%]',
        props.disabled && 'translate-y-[-50%]'
      )
    "
  >
    <!-- <div class="absolute bottom-0 left-0">
      {{ props.sku }}
    </div> -->
    <div
      :id="`pixi-app-${id}`"
      :class="
        twJoin(
          !isCustom && 'pointer-events-none',
          'flex justify-center select-none',
          isCustom &&
            width <= 1280 &&
            'h-fit overflow-hidden flex m-auto items-center',
          props.canvasHeight && 'mt-[-10px] pt-[40px]'
        )
      "
      :style="{
        height: `${props.canvasHeight ? `${props.canvasHeight - 10}px` : 'unset'}`
      }"
    />
    <div
      v-if="props.isLoading || !isReadyAddSticker.isReadyAddSticker"
      class="font-['Font_Awesome_6_Free']"
    >
      <div class="absolute w-full h-full top-0" />
      <i
        class="fa-solid fa-font-awesome !text-white absolute left-[calc(50%_-_25px)] top-[calc(50%_-_25px)] text-[50px] animate-spin"
        v-html="`${'&'}#xf110;`"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { cmyk2rgb } from '~/utils/colors'
import {
  debounce,
  find,
  findIndex,
  first,
  isUndefined,
  last,
  map,
  size,
  toNumber
} from 'lodash-es'
import {
  Text,
  type Application,
  type Container,
  type FederatedPointerEvent,
  type Graphics,
  type Renderer,
  type Sprite,
  type Texture
} from 'pixi.js'
import type { ICustomCase } from '~/pages/personalization-cases/index.vue'
import { twJoin } from 'tailwind-merge'
import type { Input } from '@pixi/ui'
import { applyTextGradient } from '~/utils/v2/pixi/personalizationUtilV2'

const { width } = useWindowSize()
const props = defineProps<{
  thumbnail?: boolean
  templateUrl?: string | null
  artworkTemplateUrl?: string
  isCustom?: boolean
  handleSaveHistory?: () => void
  disabled?: boolean
  sku: (string | undefined)[]
  canvasHeight?: number
  alreadySaveBase64?: boolean
  isLoading?: boolean
  templateMetadata?: CustomPersonalizationStateV2['templateMetadata']
}>()
const { locale } = useI18n({})
const emits = defineEmits([
  'change',
  'changeButtonDisabled',
  'updateThumbnailBase64'
])

const config = useRuntimeConfig()

const replaceMediaUrl = (url: string) => {
  return config.public.mediaUrl + url
}

// const route = useRoute()
// const savedGalleryId = computed(() => route.query.id)
const id = useId()
const isMobile = computed(() => width.value < 768)
const isIpad = computed(() => width.value < 1280)
const isReadyAddPhoto = ref(true)
const isPressShiftButton = ref(false)
const { metadata: isReadyAddSticker, toggleReady } =
  useCustomPersonalizationStoreV2()
const maxWidth = props?.isCustom ? 350 : 300

const maxHeightDefault =
  width.value < 768 && !props.isCustom ? 300 : 533 - 20 - 20
const maxHeight = maxHeightDefault

const handleSaveHistory = () => {
  props.handleSaveHistory && props.handleSaveHistory()
}

const app: Application<Renderer> = new PIXI.Application()
const text: Text = new PIXI.Text({
  text: '',
  resolution: toNumber(window?.devicePixelRatio) * 4,
  style: {
    lineHeight: 32,
    fontFamily: ['Supermarket', 'Font Awesome 6 Free'],
    padding: 1
  }
})

const personalizationStore = usePersonalizationStoreV2()
const { metadata: stateMetadata } = storeToRefs(personalizationStore)
const fontsLoadedList = ref<string[]>([])

// const userStore = useUserStore()
// const { user } = storeToRefs(userStore)

const metadata = computed(() => stateMetadata.value)

const handleUpdateTextFit = () => {
  const position = metadata.value?.textPosition || 'center'

  const safeArea = {
    center: app.stage.width * 0.808,
    left: app.stage.height * 0.519,
    right: app.stage.height * 0.519
  }

  const maxHeight = {
    center: app.stage.width * 0.3,
    left: app.stage.width * 0.3,
    right: app.stage.width * 0.3
  }

  const fx = text.width / text.height

  if (fx) {
    const width = safeArea[position as keyof typeof safeArea]
    const height = safeArea[position as keyof typeof safeArea] / fx

    text.width = width
    text.height = height

    const scaleFactor = width / height

    const newHeight = Math.min(
      height,
      safeArea[position as keyof typeof safeArea],
      maxHeight[position as keyof typeof maxHeight]
    )

    text.width = newHeight * scaleFactor
    text.height = newHeight
  }
}

const handleUpdateTextFontFamily = (fontFamily: string) => {
  text.style.fontFamily = [fontFamily, 'Supermarket', 'Font Awesome 6 Free']
}

const handleUpdateTextPosition = (positionName: string) => {
  const safeArea = {
    center: app.stage.width * 0.025,
    left: app.stage.height * 0.025,
    right: app.stage.height * 0.025
  }

  if (positionName === 'center') {
    text.rotation = 0
    text.position.set(app.stage.width / 2, app.stage.height / 2)
    text.anchor.set(0.5)
  }
  // TODO: Fix position currently is almost identical
  if (positionName == 'left') {
    text.rotation = Math.PI / 2
    text.position.set(
      text.height + app.stage.height * 0.025 + safeArea[positionName],
      app.stage.height - app.stage.height * 0.06
    )
    text.anchor.set(1, 0)
  }
  // TODO: Fix position currently is almost identical
  if (positionName == 'right') {
    text.rotation = -(Math.PI / 2)
    text.position.set(
      app.stage.width -
        text.height / 2 -
        app.stage.height * 0.025 -
        safeArea[positionName],
      app.stage.height - app.stage.height * 0.06
    )
    text.anchor.set(0, 0.5)
  }
}

const handleUpdateText = async () => {
  text.text = metadata.value.personalizeText.raw.replace(/(\r\n|\n|\r)/gm, '')
  text.position.set(app.stage.width / 2, app.stage.height / 2)
  text.anchor.set(0.5)

  const fontName = first(
    last(metadata.value.textStyle.url.split('/'))?.split('.')
  ) as string

  metadata.value.textStyle && fontName && handleUpdateTextFontFamily(fontName)
  handleUpdateTextFit()
  metadata.value.textColors.colors &&
    applyTextGradient(app, text, {
      colors: metadata.value.textColors.colors,
      rotation: metadata.value.textColors.rotation
    })
  metadata.value.textPosition &&
    handleUpdateTextPosition(metadata.value.textPosition)
}

const handleUnloadAllAssets = async () => {
  const photosList = map(photos.value, (item) => item.url)
  const stickerList = map(stickers.value, (item) => item.image)
  await Promise.all([
    deviceSelected.value?.mark_image &&
      PIXI.Assets.unload(replaceMediaUrl(deviceSelected.value?.mark_image)),
    deviceSelected.value?.case_image &&
      PIXI.Assets.unload(replaceMediaUrl(deviceSelected.value?.case_image)),
    deviceSelected.value?.device_image &&
      PIXI.Assets.unload(replaceMediaUrl(deviceSelected.value?.device_image)),
    size(stickerList) > 0 && PIXI.Assets.unload(stickerList),
    size(photosList) > 0 && PIXI.Assets.unload(photosList)
  ])
}

onBeforeUnmount(async () => {
  document.getElementById(`pixi-app-${id}`)?.removeChild(app.canvas)
  const defaultDestroyOption = {
    texture: true,
    textureSource: true,
    children: true,
    context: true,
    style: true
  }
  await Promise.all([
    backgroundColorCase?.clear(),
    backgroundStage?.clear(),
    deviceSprite.texture?.destroy(true),
    caseSprite.texture?.destroy(true),
    markSprite.texture?.destroy(true),
    containerAction?.destroy(defaultDestroyOption),
    containerSticker?.destroy(defaultDestroyOption),
    containerPhoto?.destroy(defaultDestroyOption),
    containerResize?.destroy(defaultDestroyOption),
    containerText?.destroy(defaultDestroyOption),
    caseSprite?.destroy(defaultDestroyOption),
    markSprite?.destroy(defaultDestroyOption),
    deviceSprite?.destroy(defaultDestroyOption),
    backgroundColorCase?.destroy(defaultDestroyOption),
    backgroundCaseSprite?.destroy(defaultDestroyOption),
    backgroundStage?.destroy(defaultDestroyOption),
    containerCase?.destroy(defaultDestroyOption),
    handleUnloadAllAssets(),
    PIXI.Assets?.reset(),
    PIXI.Cache?.reset()
  ])
  app?.destroy(true, defaultDestroyOption)
})

const onKeysDown = (e: KeyboardEvent) => {
  if (e.keyCode === 16 && !isPressShiftButton.value)
    isPressShiftButton.value = true
}

const onKeysUp = (e: KeyboardEvent) => {
  if (e.keyCode === 16 && isPressShiftButton.value)
    isPressShiftButton.value = false
}

const generateBase64 = async () => {
  app?.renderer.render(app.stage)
  const scaleFactor = Math.min(
    toNumber(maxWidth) / deviceSprite.texture.width,
    toNumber(maxHeight) / deviceSprite.texture.height
  )
  const base64 = await app?.renderer?.extract.image({
    target: app.stage,
    quality: 1,
    resolution: 1,
    ...(props.isCustom
      ? {
          frame: new PIXI.Rectangle(
            (maxWidth * 3) / 2 -
              (deviceSprite.texture.width / 2) * scaleFactor -
              5,
            (maxHeight * 2) / 2 -
              (deviceSprite.texture.height / 2) * scaleFactor -
              5,
            deviceSprite.texture.width * scaleFactor + 10,
            deviceSprite.texture.height * scaleFactor + 10
          )
        }
      : {})
  })
  return base64
}

onMounted(async () => {
  try {
    await app.init({
      backgroundAlpha: 0,
      width: props.isCustom ? maxWidth * 3 : maxWidth,
      height: props.isCustom ? maxHeight * 2 : maxHeight,
      roundPixels: true,
      resolution:
        toNumber(window?.devicePixelRatio) *
        (width.value >= 1280 ? 2 : props.isCustom ? 0.5 : 1),
      autoDensity: true,
      antialias: false,
      preserveDrawingBuffer: true,
      preference: 'webgl',
      webgl: { powerPreference: 'low-power' },
      powerPreference: 'low-power',
      clearBeforeRender: false
    })
    PIXI.TexturePool.textureOptions.scaleMode = 'nearest'
    app.stage.cullable = true
    app.stage.cullableChildren = true
    app.stage.eventMode = props.disabled ? 'none' : 'static'
    app.stage.hitArea = new PIXI.Rectangle(0, 0, maxWidth * 3, maxHeight * 2)

    window.addEventListener('keydown', onKeysDown)
    window.addEventListener('keyup', onKeysUp)
    document.getElementById(`pixi-app-${id}`)?.appendChild(app.canvas)
    if (props.disabled) {
      const canvas = document
        .getElementById(`pixi-app-${id}`)
        ?.getElementsByTagName('canvas')?.[0]
      if (canvas) canvas.style.touchAction = 'auto'
    }

    if (props.templateUrl) {
      const templateTexture = await PIXI.Assets.load(
        config.public.mediaUrl + '/' + props.templateUrl
      )
      const artworkTexture = await PIXI.Assets.load(
        config.public.mediaUrl + '/' + props.artworkTemplateUrl
      )

      const templateBackground = new PIXI.Sprite(templateTexture)
      const artworkTextureBackground = new PIXI.Sprite(artworkTexture)

      const textureOriginalWidth = templateBackground.texture.width
      const textureOriginalHeight = templateBackground.texture.height
      const artworkTextureOriginalWidth = artworkTextureBackground.texture.width
      const artworkTextureOriginalHeight =
        artworkTextureBackground.texture.height

      const higherResolutionFactor = 1
      const scaleFactor =
        (maxHeight / textureOriginalHeight) * higherResolutionFactor
      app.renderer.resize(
        textureOriginalWidth * scaleFactor,
        textureOriginalHeight * scaleFactor
      )

      templateBackground.position.set(
        toNumber(app?.renderer?.width) / 2,
        toNumber(app?.renderer?.height) / 2
      )
      templateBackground.anchor.set(0.5)
      templateBackground.scale.set(
        Math.min(
          toNumber(app?.renderer?.width) / textureOriginalWidth,
          toNumber(app?.renderer?.height) / textureOriginalHeight
        )
      )
      artworkTextureBackground.position.set(
        toNumber(app?.renderer?.width) / 2,
        toNumber(app?.renderer?.height) / 2
      )
      artworkTextureBackground.anchor.set(0.5)
      artworkTextureBackground.scale.set(
        Math.min(
          toNumber(app?.renderer?.width) / artworkTextureOriginalWidth,
          toNumber(app?.renderer?.height) / artworkTextureOriginalHeight
        )
      )

      app.stage.addChild(templateBackground, artworkTextureBackground)
    }
    if (!props.thumbnail) app.stage.addChild(text)

    if (props.isCustom) {
      text.alpha = 0
      await handlePrepareCustomUi()
      await handleUpdateBackgroundCaseColor()
      handlePrepareStickerUi({})
      handlePreparePhotoUi({})
      handlePrepareTextCustomUi()
    }

    app.ticker.add(async () => {
      if (!props.thumbnail) {
        handleUpdateText()
      }
      if (props.isCustom) {
        handleUpdateSticker()
        handleUpdateTextCustom()
        handleUpdatePhoto()
        handleUpdateAction()
        handleUpdateResize()
      }
    })
    app.ticker.minFPS = 60

    if (props.templateUrl) {
      const base64 = await generateBase64()
      await emits('updateThumbnailBase64', base64.src)
    }
    if (app?.renderer && (deviceSprite?.texture?.width || props.templateUrl)) {
      setInterval(async () => {
        if (!props.alreadySaveBase64) return
        if (app?.renderer) {
          await handleChangeVisibleEmptyText({ status: false })
          await handleClearFocus()
          await new Promise((resolve) => setTimeout(resolve, 200))
          const base64 = await generateBase64()
          await emits('change', base64.src)
          await handleChangeVisibleEmptyText({ status: true })
        }
      }, 500)
    }

    handleChangeAppScale()
  } catch (error) {
    console.log(error)
  }
})

type TContainerCase = Container & { id?: string }

const backgroundColorCase = new PIXI.Graphics()
const backgroundStage = new PIXI.Graphics()
const backgroundCaseSprite = new PIXI.Sprite()
let caseSprite: Sprite = new PIXI.Sprite()
let markSprite: Sprite = new PIXI.Sprite()
let deviceSprite: Sprite = new PIXI.Sprite()
const containerCase: TContainerCase = new PIXI.Container()
const containerAction: TContainerCase = new PIXI.Container()
const containerSticker: TContainerCase = new PIXI.Container()
const containerPhoto: TContainerCase = new PIXI.Container()
const containerResize: TContainerCase = new PIXI.Container()
const containerText: TContainerCase = new PIXI.Container()
const outlineResizeButton = new PIXI.Graphics()
const topLeftResizeButton = new PIXI.Graphics()
const topCenterResizeButton = new PIXI.Graphics()
const topRightResizeButton = new PIXI.Graphics()
const centerLeftResizeButton = new PIXI.Graphics()
const centerRightResizeButton = new PIXI.Graphics()
const bottomLeftResizeButton = new PIXI.Graphics()
const bottomCenterResizeButton = new PIXI.Graphics()
const bottomRightResizeButton = new PIXI.Graphics()
const copySprite = new PIXI.Sprite()
const rotateSprite = new PIXI.Sprite()
const trashSprite = new PIXI.Sprite()
const arrowsMaximizeSprite = new PIXI.Sprite()
const trashText = new PIXI.Text({
  text: locale.value == 'th' ? 'ลบ' : 'Delete',
  style: {
    fontSize: 12,
    align: 'center'
  }
})
const textSprite = new PIXI.Text({
  text: locale.value == 'th' ? 'ขยาย' : 'Expand',
  style: {
    fontSize: 12,
    align: 'center'
  }
})

const backgroundAction = new PIXI.Graphics({})

const handlePrepareCustomUi = async () => {
  try {
    emits('changeButtonDisabled', true)
    app.stage.alpha = 0
    const deviceSpriteTexture = await PIXI.Assets.load({
      src:
        (deviceSelected.value?.device_image &&
          replaceMediaUrl(deviceSelected.value?.device_image)) ||
        '',
      data: { resolution: 1 }
    }).catch((error) => console.log(error))
    const caseSpriteTexture = await PIXI.Assets.load({
      src:
        (deviceSelected.value?.case_image &&
          replaceMediaUrl(deviceSelected.value?.case_image)) ||
        '',
      data: { resolution: 1 }
    }).catch((error) => console.log(error))
    const markSpriteTexture = await PIXI.Assets.load({
      src:
        (deviceSelected.value?.mark_image &&
          replaceMediaUrl(deviceSelected.value?.mark_image)) ||
        '',
      data: { resolution: 1 }
    }).catch((error) => console.log(error))
    emits('changeButtonDisabled', false)
    await Promise.all([
      (caseSprite = new PIXI.Sprite(caseSpriteTexture || '')),
      (markSprite = new PIXI.Sprite(markSpriteTexture || '')),
      (deviceSprite = new PIXI.Sprite(deviceSpriteTexture || ''))
    ])
    app.stage.sortableChildren = true
    app.stage.alpha = 1

    if (deviceSelected.value?.case_image) {
      caseSprite.position.set(
        toNumber(maxWidth * 3) / 2,
        toNumber(maxHeight * 2) / 2
      )
      caseSprite.anchor.set(0.5)
      caseSprite.scale.set(
        Math.min(
          toNumber(maxWidth) / caseSprite.texture.width,
          toNumber(maxHeight) / caseSprite.texture.height
        )
      )
      containerCase.mask = caseSprite
    } else {
      containerCase.alpha = 0
    }
    deviceSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )
    deviceSprite.anchor.set(0.5)
    deviceSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / deviceSprite.texture.width,
        toNumber(maxHeight) / deviceSprite.texture.height
      )
    )
    markSprite.eventMode = 'none'
    markSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )
    markSprite.anchor.set(0.5)
    markSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / markSprite.texture.width,
        toNumber(maxHeight) / markSprite.texture.height
      )
    )

    containerSticker.zIndex = 3
    containerPhoto.zIndex = 2
    containerText.zIndex = 3

    containerCase.addChild(
      backgroundCaseSprite,
      backgroundColorCase,
      containerSticker,
      containerPhoto,
      containerText
    )
    containerCase.sortableChildren = true
    deviceSprite.zIndex = 1
    containerCase.zIndex = 1
    caseSprite.zIndex = 1
    containerResize.zIndex = 1
    containerAction.zIndex = 1
    markSprite.zIndex = 100
    app.stage.addChild(
      deviceSprite,
      containerCase,
      caseSprite,
      containerResize,
      containerAction,
      markSprite
    )
    props.isCustom && !props.disabled && app.stage.addChild(backgroundStage)
    containerText.id = 'container-text-id'
    containerSticker.id = 'container-sticker-id'
    containerPhoto.id = 'container-photo-id'
    containerCase.id = 'container-case-id'

    backgroundCaseSprite.eventMode = 'static'
    backgroundColorCase.eventMode = 'static'
    backgroundStage.eventMode = 'static'
    deviceSprite.eventMode = 'static'

    backgroundStage.rect(0, 0, 2000, 2000)
    backgroundStage.fill({ color: '#ffffff', alpha: 0 })

    backgroundCaseSprite.on('pointerdown', (e) => {
      activeMenu.value === 'text' && handleNewTextCustom(e)
      handleClearFocus()
    })
    deviceSprite.on('pointerdown', (e) => {
      activeMenu.value === 'text' && handleNewTextCustom(e)
      handleClearFocus()
    })
    backgroundColorCase.on('pointerdown', (e) => {
      activeMenu.value === 'text' && handleNewTextCustom(e)
      handleClearFocus()
    })
    backgroundStage.on('pointerdown', handleClearFocus)

    handlePrepareActionUi()
    handlePrepareResizeUi()
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateDevice = async () => {
  try {
    app.stage.alpha = 0
    emits('changeButtonDisabled', true)
    await Promise.all([
      deviceSprite.texture.destroy(true),
      caseSprite.texture.destroy(true),
      markSprite.texture.destroy(true)
    ])
    await Promise.all([
      (deviceSprite.texture = null as unknown as Texture),
      (caseSprite.texture = null as unknown as Texture),
      (markSprite.texture = null as unknown as Texture)
    ])
    deviceSprite.texture = await PIXI.Assets.load({
      src:
        (deviceSelected.value?.device_image &&
          replaceMediaUrl(deviceSelected.value?.device_image)) ||
        '',
      data: { resolution: 1 }
    }).catch((error) => console.log(error))

    caseSprite.texture = await PIXI.Assets.load({
      src:
        (deviceSelected.value?.case_image &&
          replaceMediaUrl(deviceSelected.value?.case_image)) ||
        '',
      data: { resolution: 1 }
    }).catch((error) => console.log(error))

    markSprite.texture = await PIXI.Assets.load({
      src:
        (deviceSelected.value?.mark_image &&
          replaceMediaUrl(deviceSelected.value?.mark_image)) ||
        '',
      data: { resolution: 1 }
    }).catch((error) => console.log(error))
    app.stage.alpha = 1

    emits('changeButtonDisabled', false)
    containerCase.mask = caseSprite
    caseSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )
    caseSprite.anchor.set(0.5)
    caseSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / caseSprite.texture.width,
        toNumber(maxHeight) / caseSprite.texture.height
      )
    )
    deviceSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )
    deviceSprite.anchor.set(0.5)
    deviceSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / deviceSprite.texture.width,
        toNumber(maxHeight) / deviceSprite.texture.height
      )
    )
    markSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )
    markSprite.anchor.set(0.5)
    markSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / markSprite.texture.width,
        toNumber(maxHeight) / markSprite.texture.height
      )
    )
    containerCase.alpha = 1
  } catch (error) {
    console.log(error)
  }
}

const handlePrepareActionUi = async () => {
  copySprite.texture = await PIXI.Assets.load({
    src: '/images/icons/copy.svg',
    data: {
      resolution:
        toNumber(window?.devicePixelRatio) * (width.value >= 1280 ? 2 : 1)
    }
  })
  copySprite.eventMode = 'static'
  copySprite.on('mousedown', handleCopy)
  copySprite.on('touchend', handleCopy)
  copySprite.height = 40
  copySprite.width = 40
  copySprite.x = copySprite.x - 2
  const copyText = new Text({
    text: locale.value == 'th' ? 'คัดลอก' : 'Copy',
    style: {
      fontSize: 12,
      align: 'center'
    }
  })
  copyText.x = locale.value == 'th' ? copySprite.x + 2 : copySprite.x + 5
  copyText.y = copySprite.y + copySprite.height - 2
  rotateSprite.texture = await PIXI.Assets.load({
    src: '/images/icons/rotate.svg',
    data: {
      resolution:
        toNumber(window?.devicePixelRatio) * (width.value >= 1280 ? 2 : 1)
    }
  })
  rotateSprite.y = 55
  rotateSprite.x = rotateSprite.x - 2
  rotateSprite.height = 40
  rotateSprite.width = 40
  rotateSprite.eventMode = 'static'
  rotateSprite.on('mousedown', handleRotate)
  rotateSprite.on('touchend', handleRotate)
  const rotateText = new Text({
    text: locale.value == 'th' ? 'หมุน' : 'Rotate',
    style: {
      fontSize: 12,
      align: 'center'
    }
  })
  rotateText.x = locale.value == 'th' ? rotateSprite.x + 8 : rotateSprite.x + 2
  rotateText.y = rotateSprite.y + rotateSprite.height - 6
  arrowsMaximizeSprite.texture = await PIXI.Assets.load({
    src: '/images/icons/arrows-maximize.svg',
    data: {
      resolution:
        toNumber(window?.devicePixelRatio) * (width.value >= 1280 ? 2 : 1)
    }
  })
  arrowsMaximizeSprite.y = 110
  arrowsMaximizeSprite.x = arrowsMaximizeSprite.x - 2
  arrowsMaximizeSprite.height = 40
  arrowsMaximizeSprite.width = 40
  arrowsMaximizeSprite.eventMode = 'static'
  arrowsMaximizeSprite.on('mousedown', handleMaximize)
  arrowsMaximizeSprite.on('touchend', handleMaximize)
  trashSprite.texture = await PIXI.Assets.load({
    src: '/images/icons/trash-red.svg',
    data: {
      resolution:
        toNumber(window?.devicePixelRatio) * (width.value >= 1280 ? 2 : 1)
    }
  })
  trashSprite.height = 40
  trashSprite.width = 40
  trashSprite.x = trashSprite.x - 3
  trashSprite.eventMode = 'static'
  textSprite.x =
    locale.value == 'th'
      ? arrowsMaximizeSprite.x + 6
      : arrowsMaximizeSprite.x + 1
  textSprite.y = arrowsMaximizeSprite.y + arrowsMaximizeSprite.height - 6
  trashText.x =
    locale.value == 'th'
      ? arrowsMaximizeSprite.x + 11.5
      : arrowsMaximizeSprite.x + 2
  trashText.y = arrowsMaximizeSprite.y + arrowsMaximizeSprite.height - 6
  trashSprite.on('mousedown', handleDelete)
  trashSprite.on('touchend', handleDelete)
  containerAction.addChild(
    backgroundAction,
    rotateText,
    copyText,
    trashText,
    textSprite,
    copySprite,
    rotateSprite,
    trashSprite,
    arrowsMaximizeSprite
  )
  containerAction.visible = false
}

const handlePrepareResizeUi = () => {
  containerResize.cullable = true
  containerResize.addChild(
    outlineResizeButton,
    topLeftResizeButton,
    topCenterResizeButton,
    topRightResizeButton,
    centerLeftResizeButton,
    centerRightResizeButton,
    bottomLeftResizeButton,
    bottomCenterResizeButton,
    bottomRightResizeButton
  )
  containerResize.visible = false
}

const onDrawResizeButton = (type: string, element: Graphics) => {
  if (
    !focusedStickerData.data &&
    !focusedPhotoData.data &&
    !focusedTextData.data
  )
    return
  const iconSize = isIpad.value ? 36 : 11
  const iconHeight = isIpad.value ? 28 : 6
  const iconWidth = 30
  const data =
    focusedStickerData.data || focusedPhotoData.data || focusedTextData.data
  if (focusedStickerData.data || focusedPhotoData.data) {
    if (type === 'top-left') {
      element.roundRect(0, 0, iconSize, iconSize, 0)
      element.pivot.set(iconSize / 2, iconSize / 2)
      element.position.set(0, 0)
    } else if (type === 'top-center') {
      element.roundRect(0, 0, iconWidth, iconHeight, 0)
      element.pivot.set(iconWidth / 2, iconHeight / 2)
      element.position.set(toNumber(data?.width) / 2, -2)
    } else if (type === 'top-right') {
      element.roundRect(0, 0, iconSize, iconSize, 0)
      element.pivot.set(iconSize / 2, iconSize / 2)
      element.position.set(toNumber(data?.width), 0)
    } else if (type === 'center-left') {
      element.roundRect(0, 0, iconHeight, iconWidth, 0)
      element.pivot.set(iconHeight / 2, iconWidth / 2)
      element.position.set(-2, toNumber(data?.height) / 2)
    } else if (type === 'center-right') {
      element.roundRect(0, 0, iconHeight, iconWidth, 0)
      element.pivot.set(iconHeight / 2, iconWidth / 2)
      element.position.set(
        toNumber(data?.width) + 2,
        toNumber(data?.height) / 2
      )
    } else if (type === 'bottom-left') {
      element.roundRect(0, 0, iconSize, iconSize, 0)
      element.pivot.set(iconSize / 2, iconSize / 2)
      element.position.set(0, toNumber(data?.height))
    } else if (type === 'bottom-center') {
      element.roundRect(0, 0, iconWidth, iconHeight, 0)
      element.pivot.set(iconWidth / 2, iconHeight / 2)
      element.position.set(
        toNumber(data?.width) / 2,
        toNumber(data?.height) + 2
      )
    } else if (type === 'bottom-right') {
      element.roundRect(0, 0, iconSize, iconSize, 0)
      element.pivot.set(iconSize / 2, iconSize / 2)
      element.position.set(toNumber(data?.width), toNumber(data?.height))
    } else return
  }
  element.stroke({ width: 2, color: 0x0989ff }).fill(0xffffff)
  element.eventMode = 'static'
  element.on('pointerdown', (event) => onDragResizeStart(event, type, element))
}

const onDragResizeEnd = (event: FederatedPointerEvent, element: Graphics) => {
  event.stopImmediatePropagation()
  element.hitArea = null
  focusedResizeData.data = undefined
  handleSaveHistory()
}

const onDragResizeStart = (
  event: FederatedPointerEvent,
  type: string,
  element: Graphics
) => {
  event.stopImmediatePropagation()
  focusedResizeData.data = element
  element.hitArea = new PIXI.Rectangle(
    -maxWidth * 3,
    -maxHeight * 2,
    maxWidth * 3 * 2,
    maxHeight * 2 * 2
  )
  element.on('mouseup', (event) => onDragResizeEnd(event, element))
  element.on('touchend', (event) => onDragResizeEnd(event, element))
  element.on('pointerupoutside', (event) => onDragResizeEnd(event, element))
  element.on('touchendoutside', (event) => onDragResizeEnd(event, element))
  element.on('pointermove', (event) => onDragResizeMove(event, type))
}

const stickerIndex = computed(() =>
  stickers.value?.findIndex((item) => item?.id === focusedStickerData.data?.id)
)
const photoIndex = computed(() =>
  photos.value?.findIndex((item) => item?.id === focusedPhotoData.data?.id)
)
const textIndex = computed(() =>
  texts.value?.findIndex((item) => item?.id === focusedTextData.data?.id)
)

const onDragResizeMove = (event: FederatedPointerEvent, type: string) => {
  event.stopImmediatePropagation()
  if (
    !focusedResizeData.data ||
    (!focusedStickerData.data &&
      !focusedPhotoData.data &&
      !focusedTextData.data)
  )
    return

  const data =
    focusedStickerData.data || focusedPhotoData.data || focusedTextData.data
  let newWidth = ['bottom-right', 'top-right', 'center-right'].includes(type)
    ? event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
    : -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5

  let newHeight = ['bottom-right', 'bottom-center', 'bottom-left'].includes(
    type
  )
    ? event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
    : -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5

  if (focusedStickerData.data) {
    if (stickerIndex.value === -1) return
    if (focusedStickerData?.data?.rotation === 0) {
      if (newWidth < 20 || newHeight < 20) return
      if (
        ['bottom-right', 'bottom-left', 'top-left', 'top-right'].includes(type)
      ) {
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newWidth
        })
      } else if (['top-center', 'bottom-center'].includes(type)) {
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left', 'center-right'].includes(type)) {
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedStickerData?.data?.rotation > 0 &&
      focusedStickerData?.data?.rotation < Math.PI / 2
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
    if (focusedStickerData?.data?.rotation === Math.PI / 2) {
      if (['top-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedStickerData?.data?.rotation > Math.PI / 2 &&
      focusedStickerData?.data?.rotation < Math.PI
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
    if (focusedStickerData?.data?.rotation === Math.PI) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedStickerData?.data?.rotation > Math.PI &&
      focusedStickerData?.data?.rotation < (3 * Math.PI) / 2
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
    if (focusedStickerData?.data?.rotation === (3 * Math.PI) / 2) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedStickerData?.data?.rotation > (3 * Math.PI) / 2 &&
      focusedStickerData?.data?.rotation < 2 * Math.PI
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(stickers.value[stickerIndex.value], {
          width: newWidth
        })
      }
    }
  } else if (focusedPhotoData.data) {
    if (photoIndex.value === -1) return
    if (focusedPhotoData?.data?.rotation === 0) {
      if (newWidth < 20 || newHeight < 20) return
      if (
        ['bottom-right', 'bottom-left', 'top-left', 'top-right'].includes(type)
      ) {
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newWidth / photos.value[photoIndex.value].aspectRatio
        })
      } else if (['top-center', 'bottom-center'].includes(type)) {
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left', 'center-right'].includes(type)) {
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedPhotoData?.data?.rotation > 0 &&
      focusedPhotoData?.data?.rotation < Math.PI / 2
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
    if (focusedPhotoData?.data?.rotation === Math.PI / 2) {
      if (['top-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedPhotoData?.data?.rotation > Math.PI / 2 &&
      focusedPhotoData?.data?.rotation < Math.PI
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
    if (focusedPhotoData?.data?.rotation === Math.PI) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          event.global.y - toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          -event.global.y + toNumber(data?.y) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedPhotoData?.data?.rotation > Math.PI &&
      focusedPhotoData?.data?.rotation < (3 * Math.PI) / 2
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
    if (focusedPhotoData?.data?.rotation === (3 * Math.PI) / 2) {
      if (['top-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
    if (
      focusedPhotoData?.data?.rotation > (3 * Math.PI) / 2 &&
      focusedPhotoData?.data?.rotation < 2 * Math.PI
    ) {
      if (['top-left'].includes(type)) {
        const newWidth =
          -event.global.x + toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['bottom-right'].includes(type)) {
        const newWidth =
          event.global.x - toNumber(data?.x) + toNumber(data?.width) * 0.5
        const newHeight = newWidth / photos.value[photoIndex.value].aspectRatio
        if (newWidth < 20 || newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth,
          height: newHeight
        })
      } else if (['top-center'].includes(type)) {
        const newHeight =
          -event.global.x + toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['bottom-center'].includes(type)) {
        const newHeight =
          event.global.x - toNumber(data?.x) + toNumber(data?.height) * 0.5
        if (newHeight < 20) return
        Object.assign(photos.value[photoIndex.value], {
          height: newHeight
        })
      } else if (['center-left'].includes(type)) {
        const newWidth =
          event.global.y - toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      } else if (['center-right'].includes(type)) {
        const newWidth =
          -event.global.y + toNumber(data?.y) + toNumber(data?.width) * 0.5
        if (newWidth < 20) return
        Object.assign(photos.value[photoIndex.value], {
          width: newWidth
        })
      }
    }
  } else if (focusedTextData.data) {
    if (textIndex.value === -1) return
    if (focusedTextData.data.rotation === -(Math.PI * 1.5)) {
      newWidth = ['bottom-right', 'top-right'].includes(type)
        ? event.global.y - toNumber(data?.y) + toNumber(data?.width) / 2
        : -event.global.y + toNumber(data?.y) + toNumber(data?.width) / 2

      newHeight = ['top-right', 'top-left'].includes(type)
        ? event.global.x - toNumber(data?.x) + toNumber(data?.height) / 2
        : -event.global.x + toNumber(data?.x) + toNumber(data?.height) / 2
    } else if (focusedTextData.data.rotation === Math.PI * 1.5) {
      newWidth = ['bottom-left', 'top-left'].includes(type)
        ? event.global.y - toNumber(data?.y) + toNumber(data?.width) / 2
        : -event.global.y + toNumber(data?.y) + toNumber(data?.width) / 2

      newHeight = ['bottom-right', 'bottom-left'].includes(type)
        ? event.global.x - toNumber(data?.x) + toNumber(data?.height) / 2
        : -event.global.x + toNumber(data?.x) + toNumber(data?.height) / 2
    }
    if (newWidth < 20 || newHeight < 20) return
    if (
      ['bottom-right', 'bottom-left', 'top-left', 'top-right'].includes(type)
    ) {
      Object.assign(texts.value[textIndex.value], {
        width: newWidth,
        height: newHeight
      })
    }
  }
}

const textFocusedData = computed(() =>
  find(texts.value, (item) => item?.id === focusedTextData.data?.id)
)

const photoFocusedData = computed(() =>
  find(photos.value, (item) => item?.id === focusedPhotoData.data?.id)
)

const handleUpdateResize = async () => {
  try {
    topLeftResizeButton?.clear()
    topCenterResizeButton?.clear()
    topRightResizeButton?.clear()
    centerLeftResizeButton?.clear()
    centerRightResizeButton?.clear()
    bottomLeftResizeButton?.clear()
    bottomCenterResizeButton?.clear()
    bottomRightResizeButton?.clear()
    outlineResizeButton?.clear()
    if (
      !focusedStickerData.data &&
      !focusedTextData.data &&
      !focusedPhotoData.data
    )
      return

    if (focusedStickerData.data || focusedPhotoData.data) {
      const data = focusedStickerData.data || focusedPhotoData.data
      outlineResizeButton
        .roundRect(
          -3,
          -3,
          toNumber(data?.width) + 5,
          toNumber(data?.height) + 5,
          0
        )
        .stroke({ width: 2, color: 0x0989ff })
      containerResize.pivot.set(
        toNumber(data?.width) / 2,
        toNumber(data?.height) / 2
      )
      containerResize.position.set(toNumber(data?.x), toNumber(data?.y))
      containerResize.rotation = data?.rotation || 0

      onDrawResizeButton('top-left', topLeftResizeButton)
      onDrawResizeButton('top-center', topCenterResizeButton)
      onDrawResizeButton('top-right', topRightResizeButton)
      onDrawResizeButton('center-left', centerLeftResizeButton)
      onDrawResizeButton('center-right', centerRightResizeButton)
      onDrawResizeButton('bottom-left', bottomLeftResizeButton)
      onDrawResizeButton('bottom-center', bottomCenterResizeButton)
      onDrawResizeButton('bottom-right', bottomRightResizeButton)
    } else if (focusedTextData.data) {
      if (!textFocusedData.value) return
      outlineResizeButton
        .roundRect(
          0,
          0,
          toNumber(textFocusedData.value?.width),
          toNumber(textFocusedData.value?.height),
          0
        )
        .stroke({ width: 2, color: 0x0989ff })
      containerResize.pivot.set(
        toNumber(textFocusedData.value?.width) / 2,
        toNumber(textFocusedData.value?.height) / 2
      )
      containerResize.position.set(
        toNumber(textFocusedData.value?.x),
        toNumber(textFocusedData.value?.y)
      )
      containerResize.rotation = focusedTextData.data.rotation
    }
    containerResize.visible = true
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateSticker = async () => {
  try {
    stickers?.value?.forEach((item) => {
      const data = app.stage?.children
        .find((child: TContainerCase) => child?.id === 'container-case-id')
        ?.children?.find(
          (child: TContainerCase) => child?.id === 'container-sticker-id'
        )
        ?.children?.find((i: TContainerCase) => i.id === item.id)
      if (data) {
        data.rotation = item.rotation
        data.x = item.x
        data.y = item.y
        data.width = item.width
        data.height = item.height
      }
    })

    app.stage?.children
      .find((child: TContainerCase) => child?.id === 'container-case-id')
      ?.children?.find(
        (child: TContainerCase) => child?.id === 'container-sticker-id'
      )
      ?.children?.forEach((item: TContainerCase) => {
        const data = item?.id
          ? stickers.value?.map((i) => i.id)?.includes(item.id)
          : undefined
        if (!data) {
          app.stage?.children
            .find((child: TContainerCase) => child?.id === 'container-case-id')
            ?.children?.find(
              (child: TContainerCase) => child?.id === 'container-sticker-id'
            )
            ?.removeChild(item)
          handleClearFocus()
        }
      })
  } catch (error) {
    console.log(error)
  }
}

const handleUpdatePhoto = () => {
  try {
    const photoContainer: Container | undefined = app.stage?.children
      ?.find((child: TContainerCase) => child?.id === 'container-case-id')
      ?.children?.find(
        (child: TContainerCase) => child?.id === 'container-photo-id'
      )
    photos?.value?.forEach((item) => {
      const data = photoContainer?.children?.find(
        (i: TContainerCase) => i.id === item.id
      )
      if (data) {
        data.x = item.x
        data.y = item.y
        data.width = item.width
        data.height = item.height
        data.rotation = item.rotation
      }
    })

    photoContainer?.children?.forEach((item: TContainerCase) => {
      const data = item?.id
        ? photos.value?.map((i) => i.id)?.includes(item.id)
        : undefined
      if (!data) {
        app.stage?.children
          .find((child: TContainerCase) => child?.id === 'container-case-id')
          ?.children?.find(
            (child: TContainerCase) => child?.id === 'container-photo-id'
          )
          ?.removeChild(item)
        handleClearFocus()
      }
    })
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateTextCustom = () => {
  try {
    texts?.value?.forEach((item, index) => {
      const data = app.stage?.children
        .find((child: TContainerCase) => child?.id === 'container-case-id')
        ?.children?.find(
          (child: TContainerCase) => child?.id === 'container-text-id'
        )
        ?.children?.find((i: TContainerCase) => i.id === item.id)
      const newWidth =
        toNumber(
          (data as TCustomInput & { inputField: { width: number } })?.inputField
            ?.width
        ) < 200
          ? 200
          : toNumber(
              (data as TCustomInput & { inputField: { width: number } })
                ?.inputField?.width
            ) + 20
      if (data) {
        data.x = item.x
        data.y = item.y
        data.pivot.set(toNumber(newWidth) / 2, toNumber(item.height) / 2)
        data.width = toNumber(newWidth)
        data.height = toNumber(item.height)
        data.rotation = item.rotation
        Object.assign(texts.value[index], {
          width: newWidth
        })

        if (focusedTextData?.data?.id === item.id) {
          focusedTextData.data.rotation = item.rotation
          focusedTextData.data.x = item.x
          focusedTextData.data.y = item.y
          focusedTextData.data.width = newWidth
        }
      }
    })
  } catch (error) {
    console.log(error)
  }
}

const handleCopy = () => {
  if (focusedStickerData.data) {
    const newSticker = {
      x: (maxWidth * 3) / 2 - 20,
      y: ((maxHeight - 20 - 20) * 5) / 4 + 50,
      width: focusedStickerData.data?.width || 100,
      height: focusedStickerData.data?.height || 100,
      image: focusedStickerData.data?.image || '',
      rotation: focusedStickerData.data?.rotation || 0,
      id: `${Math.floor(Math.random() * 100000000000)}`,
      sku: focusedStickerData.data?.sku
    }
    stickers.value.push(newSticker)
    focusedStickerData.data = newSticker as unknown as TCustomSprite
    focusedPhotoData.data = undefined
    focusedTextData.data = undefined
    handlePrepareStickerUi({})
    handleSaveHistory()
  } else if (focusedTextData.data) {
    if (!textFocusedData.value) return
    const newText = {
      id: `${Math.floor(Math.random() * 100000000000)}`,
      x: (maxWidth * 3) / 2 - 20,
      y: ((maxHeight - 20 - 20) * 5) / 4 + 50,
      width: focusedTextData.data?.width,
      height: 85,
      text: focusedTextData.data?.value,
      rotation: focusedTextData.data?.rotation,
      color: textFocusedData.value?.color,
      font_type: textFocusedData.value?.font_type,
      font_size: textFocusedData.value?.font_size,
      custom_color: textFocusedData.value?.custom_color,
      position_type: textFocusedData.value?.position_type
    }
    texts.value.push(newText)
    focusedStickerData.data = undefined
    focusedPhotoData.data = undefined
    focusedTextData.data = newText as unknown as TCustomInput
    resetFieldFocusedText({ value: { id: newText.id } })
    handlePrepareTextCustomUi()
    handleSaveHistory()
  } else if (focusedPhotoData.data) {
    if (!photoFocusedData.value) return
    const newPhoto = {
      id: `${Math.floor(Math.random() * 100000000000)}`,
      x: (maxWidth * 3) / 2 - 20,
      y: ((maxHeight - 20 - 20) * 5) / 4 + 50,
      width: focusedPhotoData?.data?.width,
      height: focusedPhotoData?.data?.height,
      type: photoFocusedData.value?.type,
      url: photoFocusedData.value?.url,
      aspectRatio: photoFocusedData.value?.aspectRatio,
      rotation: 0
    }
    photos.value.push(newPhoto)
    focusedStickerData.data = undefined
    focusedPhotoData.data = newPhoto as unknown as TCustomSprite
    focusedTextData.data = undefined
    handlePreparePhotoUi({})
    handleSaveHistory()
  }
}

const { on } = useEventTracking()

const handleNewTextCustom = (e?: FederatedPointerEvent) => {
  try {
    if (
      texts.value?.[0]?.id ||
      !props.templateMetadata?.text?.fontFamily?.[0]?.name
    )
      return
    on('add_text', {
      font_style: props.templateMetadata?.text?.fontFamily?.[0]?.name,
      font_size: 32,
      font_position: {
        x: toNumber(e?.global?.x || 0),
        y: toNumber(e?.global?.y || 0)
      },
      color: '#211E1C'
    })
    const id = `${Math.floor(Math.random() * 100000000000)}`
    texts.value = [
      {
        id,
        x: toNumber(e?.global?.x || (maxWidth * 3) / 2),
        y: toNumber(e?.global?.y || ((maxHeight - 20 - 20) * 5) / 4 + 60),
        width: 200,
        height: 85,
        text: '',
        rotation: 0,
        color: '#211E1C',
        font_type: props.templateMetadata?.text?.fontFamily?.[0]?.name,
        font_size: 32,
        custom_color: '',
        position_type: ''
      }
    ]
    focusedPhotoData.data = undefined
    focusedStickerData.data = undefined
    resetFieldFocusedText({ value: { id } })
    focusedTextData.data = texts.value?.[0] as unknown as TCustomInput
    isUndefined(e) && resetFieldFocusedText({ value: { id } })
    handleSaveHistory()
  } catch (error) {
    console.log(error)
  }
}

const debouncedTextChange = debounce(
  async (nextState: {
    text_id: string
    new_content: string
    font_style: string
    font_size: number
    font_position: {
      x: number
      y: number
    }
    color: string
  }) => {
    on('edit_text', {
      ...nextState
    })
  },
  1000
)

const onTapTime = ref(0)

const handlePrepareTextCustomUi = async () => {
  try {
    const borderColor = '#DCB000'
    const backgroundColor = '#F1D583'

    containerText.removeChildren()
    for await (const [index, item] of texts.value.entries()) {
      try {
        if (!item?.font_type) continue
        const inputWidth = item.width || 0
        const inputHeight = item.height || 0

        const input: TCustomInput = new PIXIUI.Input({
          bg: new PIXI.Graphics()
            .fill({ color: borderColor, alpha: 0 })
            .roundRect(0, 0, inputWidth, inputHeight, 0)
            .fill({ color: backgroundColor, alpha: 0 })
            .roundRect(0, 0, inputWidth, inputHeight, 0),
          nineSliceSprite: [100, 0, 100, 0],
          align:
            item.position_type === 'bottom-left'
              ? 'left'
              : item.position_type === 'bottom-right'
                ? 'right'
                : 'center',
          textStyle: {
            fontFamily: [item.font_type, 'Supermarket', 'Font Awesome 6 Free'],
            fontSize: `${item.font_size}px`,
            fill: item.custom_color || item.color,
            lineHeight: 100,
            padding: 3
          },
          padding: {
            left: 5
          },
          value: item.text || '',
          placeholder: 'Enter ...'
        })
        const caseSpriteScale = Math.min(
          toNumber(maxWidth) / caseSprite.texture.width,
          toNumber(maxHeight) / caseSprite.texture.height
        )
        if (item.x === 0 && item.y === 0) {
          if (['rotate-left', 'left'].includes(item.position_type)) {
            Object.assign(texts.value[index], {
              x:
                toNumber(maxWidth * 3) / 2 -
                (caseSprite.texture.width / 2) * caseSpriteScale +
                50,
              y: toNumber(maxHeight * 2) / 2
            })
          } else if (['rotate-right', 'right'].includes(item.position_type)) {
            Object.assign(texts.value[index], {
              x:
                toNumber(maxWidth * 3) / 2 +
                (caseSprite.texture.width / 2) * caseSpriteScale -
                50,
              y: toNumber(maxHeight * 2) / 2
            })
          } else if (item.position_type === 'center') {
            Object.assign(texts.value[index], {
              x: toNumber(maxWidth * 3) / 2,
              y: toNumber(maxHeight * 2) / 2
            })
          } else if (
            ['bottom-left', 'bottom-center', 'bottom-right'].includes(
              item.position_type
            )
          ) {
            Object.assign(texts.value[index], {
              x: toNumber(maxWidth * 3) / 2,
              y:
                toNumber(maxHeight * 2) / 2 +
                toNumber(caseSprite.height) / 2 -
                70
            })
          }
        }

        input.rotation = item.rotation
        input.id = item.id

        let lastTap = 0
        input.onChange.connect((text) => {
          if (width.value <= 1024 && isPressShiftButton.value) {
            const curTime = new Date().getTime()
            const tapLen = curTime - lastTap
            if (tapLen < 100 && tapLen > 0) {
              const newText =
                text.slice(-2, -1) === text.slice(-1) && text.slice(-1)
                  ? text.slice(0, -1)
                  : text
              input.value = newText
              input.value = newText
              Object.assign(texts.value[index], {
                text: newText
              })
              debouncedTextChange({
                text_id: texts.value[index].id,
                new_content: newText,
                font_style: texts.value[index]?.font_type,
                font_size: texts.value[index]?.font_size,
                font_position: {
                  x: texts.value[index].x,
                  y: texts.value[index].y
                },
                color: texts.value[index].color
              })
              handleSaveHistory()
            } else {
              Object.assign(texts.value[index], {
                text: input.value
              })
              debouncedTextChange({
                text_id: texts.value[index].id,
                new_content: input.value,
                font_style: texts.value[index]?.font_type,
                font_size: texts.value[index]?.font_size,
                font_position: {
                  x: texts.value[index].x,
                  y: texts.value[index].y
                },
                color: texts.value[index].color
              })
              handleSaveHistory()
            }
            lastTap = curTime
            return
          }
          Object.assign(texts.value[index], {
            text: input.value
          })
          debouncedTextChange({
            text_id: texts.value[index].id,
            new_content: input.value,
            font_style: texts.value[index]?.font_type,
            font_size: texts.value[index]?.font_size,
            font_position: {
              x: texts.value[index].x,
              y: texts.value[index].y
            },
            color: texts.value[index].color
          })
          handleSaveHistory()
        })
        input.on('pointerdown', (e) => onDragTextStart(e, input))
        input.pivot.set(toNumber(item.width) / 2, toNumber(item.height) / 2)
        input.x = item.x
        input.y = item.y
        input.scale = 1
        containerText.addChild(input)
      } catch (error) {
        console.log(error)
      }
    }
  } catch (error) {
    console.log(error)
  }
}

const onDragTextStart = (
  event: FederatedPointerEvent,
  element: TCustomInput
) => {
  onTapTime.value = new Date().getTime()
  event.stopImmediatePropagation()
  handleClearFocus()
  selectedData.component = {
    x: element.x,
    y: element.y
  }
  selectedData.id = element.id || ''
  selectedData.x = event.global.x
  selectedData.y = event.global.y
  selectedData.dragging = true
  selectedData.type = 'text'

  focusedTextData.data = element
  resetFieldFocusedText({ value: { id: element.id || '' } })
  app.stage.on('pointermove', (e) => onDragSelectedMove(e, element))
  element.on('mouseup', onDragSelectedEnd)
  element.on('touchend', onDragSelectedEnd)
  element.on('pointerupoutside', onDragSelectedEnd)
  element.on('touchendoutside', onDragSelectedEnd)

  const index = findIndex(texts.value, { id: element.id || '' })

  debouncedTextChange({
    text_id: texts.value[index].id,
    new_content: texts.value[index]?.text,
    font_style: texts.value[index]?.font_type,
    font_size: texts.value[index]?.font_size,
    font_position: {
      x: event.global.x,
      y: event.global.y
    },
    color: texts.value[index].color
  })
}

const onDragSelectedMove = (
  event: FederatedPointerEvent,
  _element: TCustomInput | TCustomSprite
) => {
  event.stopImmediatePropagation()
  if (!selectedData.dragging) return

  const caseSpriteScale = Math.min(
    toNumber(maxWidth) / caseSprite.texture.width,
    toNumber(maxHeight) / caseSprite.texture.height
  )
  app.stage.hitArea = new PIXI.Rectangle(
    toNumber(maxWidth * 3) / 2 -
      (toNumber(caseSprite?.texture?.width) / 2) * caseSpriteScale,
    toNumber(maxHeight * 2) / 2 -
      (toNumber(caseSprite?.texture?.height) / 2) * caseSpriteScale,
    toNumber(caseSprite?.texture?.width) * caseSpriteScale,
    toNumber(caseSprite?.texture?.height) * caseSpriteScale
  )

  const offsetX = event.global.x - selectedData.x + selectedData.component.x
  const offsetY = event.global.y - selectedData.y + selectedData.component.y

  const newPosition = {
    x: offsetX,
    y: offsetY
  }

  if (selectedData.type === 'text') {
    if (textIndex.value === -1) return

    Object.assign(texts.value[textIndex.value], {
      ...newPosition,
      position_type: ''
    })
  } else if (selectedData.type === 'sticker') {
    if (stickerIndex.value === -1) return

    Object.assign(stickers.value[stickerIndex.value], newPosition)
  } else if (selectedData.type === 'photo') {
    if (photoIndex.value === -1) return

    Object.assign(photos.value[photoIndex.value], newPosition)
  }
}
interface NavigatorWithVirtualKeyboard extends Navigator {
  virtualKeyboard?: {
    hide: () => void
  }
}
const onDragSelectedEnd = (event: FederatedPointerEvent) => {
  event.stopImmediatePropagation()
  app.stage.hitArea = new PIXI.Rectangle(0, 0, maxWidth * 3, maxHeight * 2)
  selectedData.dragging = false
  handleSaveHistory()
  const customNavigator = navigator as NavigatorWithVirtualKeyboard
  if (
    new Date().getTime() - onTapTime.value > 190 &&
    customNavigator.virtualKeyboard &&
    typeof customNavigator.virtualKeyboard.hide === 'function'
  ) {
    customNavigator.virtualKeyboard.hide()
  }
}

const handlePrepareStickerUi = async ({
  oldValue,
  newValue
}: {
  oldValue?: ICustomCase['stickers']
  newValue?: ICustomCase['stickers']
}) => {
  try {
    if (!isReadyAddSticker.isReadyAddSticker) return
    toggleReady(false)
    emits('changeButtonDisabled', true)
    for await (const item of stickers.value) {
      if (
        app.stage?.children
          .find((child: TContainerCase) => child?.id === 'container-case-id')
          ?.children?.find(
            (child: TContainerCase) => child?.id === 'container-sticker-id'
          )
          ?.children?.some((child: TContainerCase) => child.id === item.id)
      ) {
        continue
      }

      const texture = await PIXI.Assets.load({
        src: config.public.mediaUrl + item.image,
        data: { resolution: 1 }
      })
      const stickerSprite: TCustomSprite = new PIXI.Sprite(texture)
      stickerSprite.anchor = 0.5
      stickerSprite.width = item.width
      stickerSprite.height = item.height
      stickerSprite.x = item.x
      stickerSprite.y = item.y
      stickerSprite.id = item.id
      stickerSprite.rotation = item.rotation
      stickerSprite.image = item.image
      stickerSprite.sku = item.sku
      stickerSprite.cullable = true
      stickerSprite.eventMode = 'static'

      stickerSprite.on('pointerdown', (e) =>
        onDragStickerStart(e, stickerSprite)
      )
      stickerSprite.on('mouseup', onDragSelectedEnd)
      stickerSprite.on('touchend', onDragSelectedEnd)
      stickerSprite.on('pointerupoutside', onDragSelectedEnd)
      stickerSprite.on('touchendoutside', onDragSelectedEnd)

      containerSticker.addChild(stickerSprite)
    }
    toggleReady(true)
    if (oldValue && newValue && size(newValue) > size(oldValue)) {
      focusedTextData.data = undefined
      focusedPhotoData.data = undefined
      focusedStickerData.data = newValue?.[
        size(newValue) - 1
      ] as unknown as TCustomSprite
    }
    emits('changeButtonDisabled', false)
  } catch (error) {
    console.log(error)
  }
}

const handlePreparePhotoUi = async ({
  oldValue,
  newValue
}: {
  oldValue?: ICustomCase['photos']
  newValue?: ICustomCase['photos']
}) => {
  try {
    if (!isReadyAddPhoto.value) return
    isReadyAddPhoto.value = false
    emits('changeButtonDisabled', true)
    for await (const item of photos.value) {
      if (
        app.stage?.children
          .find((child: TContainerCase) => child?.id === 'container-case-id')
          ?.children?.find(
            (child: TContainerCase) => child?.id === 'container-photo-id'
          )
          ?.children?.some((child: TContainerCase) => child.id === item.id)
      ) {
        continue
      }

      const texture = await PIXI.Assets.load({
        src: item.url,
        crossorigin: 'anonymous',
        data: { resolution: 1 }
      })
      const photoSprite: TCustomSprite = new PIXI.Sprite(texture)
      photoSprite.anchor = 0.5
      photoSprite.width = item.width
      photoSprite.height = item.height
      photoSprite.x = item.x
      photoSprite.y = item.y
      photoSprite.id = item.id
      photoSprite.rotation = item.rotation
      photoSprite.cullable = true
      photoSprite.eventMode = 'static'

      photoSprite.on('mousedown', (e) => onDragPhotoStart(e, photoSprite))
      photoSprite.on('touchstart', (e) => onDragPhotoStart(e, photoSprite))
      photoSprite.on('mouseup', onDragSelectedEnd)
      photoSprite.on('touchend', onDragSelectedEnd)
      photoSprite.on('pointerupoutside', onDragSelectedEnd)
      photoSprite.on('touchendoutside', onDragSelectedEnd)

      containerPhoto.addChild(photoSprite)
    }
    isReadyAddPhoto.value = true
    if (oldValue && newValue && size(newValue) > size(oldValue)) {
      focusedTextData.data = undefined
      focusedStickerData.data = undefined
      focusedPhotoData.data = newValue?.[
        size(newValue) - 1
      ] as unknown as TCustomSprite
    }
    emits('changeButtonDisabled', false)
  } catch (error) {
    console.log(error)
  }
}

type TCustomSprite = Sprite & { id?: string; image?: string; sku?: string }
type TCustomInput = Input & { id?: string }

const focusedStickerData = reactive<{ data: TCustomSprite | undefined }>({
  data: undefined
})
const focusedPhotoData = reactive<{ data: TCustomSprite | undefined }>({
  data: undefined
})
const focusedTextData = reactive<{ data: TCustomInput | undefined }>({
  data: undefined
})

const focusedResizeData = reactive<{ data: Graphics | undefined }>({
  data: undefined
})

const selectedData = reactive({
  x: 0,
  y: 0,
  dragging: false,
  component: { x: 0, y: 0 },
  id: '',
  type: ''
})

const handleChangeVisibleEmptyText = ({ status }: { status: boolean }) => {
  texts?.value?.forEach((item) => {
    const data = app.stage?.children
      .find((child: TContainerCase) => child?.id === 'container-case-id')
      ?.children?.find(
        (child: TContainerCase) => child?.id === 'container-text-id'
      )
      ?.children?.find((i: TContainerCase) => i.id === item.id)
    if (data && !item.text) {
      data.visible = status
    }
  })
}

const handleClearFocus = () => {
  focusedTextData.data = undefined
  focusedStickerData.data = undefined
  focusedPhotoData.data = undefined
  containerAction.visible = false
  selectedData.dragging = false
  resetFieldFocusedText({ value: { id: '' } })
}

const handleRotate = () => {
  if (
    !focusedStickerData.data &&
    !focusedTextData.data &&
    !focusedPhotoData.data
  )
    return

  if (focusedStickerData.data) {
    if (stickerIndex.value === -1) return
    Object.assign(stickers.value[stickerIndex.value], {
      rotation:
        stickers.value[stickerIndex.value].rotation + Math.PI / 10 > 6
          ? 0
          : stickers.value[stickerIndex.value].rotation + Math.PI / 10
    })
  } else if (focusedTextData.data) {
    if (textIndex.value === -1) return
    Object.assign(texts.value[textIndex.value], {
      rotation:
        texts.value[textIndex.value].rotation + Math.PI / 10 > 6
          ? 0
          : texts.value[textIndex.value].rotation + Math.PI / 10
    })
  } else if (focusedPhotoData.data) {
    if (photoIndex.value === -1) return
    Object.assign(photos.value[photoIndex.value], {
      rotation:
        photos.value[photoIndex.value].rotation + Math.PI / 10 > 6
          ? 0
          : photos.value[photoIndex.value].rotation + Math.PI / 10
    })
  }
  handleSaveHistory()
}

const handleMaximize = () => {
  if (!focusedPhotoData.data) return

  if (photoIndex.value === -1) return
  const newWidth = Math.max(
    caseSprite.width,
    caseSprite.height * photos.value[photoIndex.value].aspectRatio
  )
  const newHeight = Math.max(
    caseSprite.width / photos.value[photoIndex.value].aspectRatio,
    caseSprite.height
  )
  Object.assign(photos.value[photoIndex.value], {
    x: caseSprite.x,
    y: caseSprite.y,
    width: newWidth,
    height: newHeight
  })
  handleSaveHistory()
}

const handleDelete = () => {
  if (focusedStickerData.data) {
    stickers.value = stickers.value.filter(
      (item) => item.id !== focusedStickerData.data?.id
    )
    handleSaveHistory()
  } else if (focusedTextData.data) {
    texts.value = texts.value.filter(
      (item) => item.id !== focusedTextData.data?.id
    )
    handlePrepareTextCustomUi()
    handleSaveHistory()
  } else if (focusedPhotoData.data) {
    photos.value = photos.value.filter(
      (item) => item.id !== focusedPhotoData.data?.id
    )
    handleSaveHistory()
  }
  handleClearFocus()
}
const onKeyDown = (event: KeyboardEvent) => {
  if (
    event.key === 'Delete' ||
    (event.key === 'Backspace' && !focusedTextData.data)
  ) {
    handleDelete()
  }
  if (event.key === 'Delete' && focusedTextData.data) {
    handleDelete()
  }
}
onMounted(() => {
  window.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown)
})
const handleUpdateAction = async () => {
  try {
    backgroundAction?.clear()
    if (
      !focusedStickerData.data &&
      !focusedTextData.data &&
      !focusedPhotoData.data
    )
      return
    if (focusedTextData.data && textIndex.value === -1) {
      containerAction.visible = false
      return
    }

    containerAction.visible = true

    const data =
      focusedStickerData.data || focusedPhotoData.data || focusedTextData.data
    const isSkew = [
      Math.PI / 4,
      (3 * Math.PI) / 4,
      (5 * Math.PI) / 4,
      (7 * Math.PI) / 4
    ].includes(toNumber(data?.rotation || 0))
    const isRevert = [Math.PI / 2, (3 * Math.PI) / 2].includes(
      toNumber(data?.rotation || 0)
    )
    const positionX =
      toNumber(data?.x) +
      (isSkew
        ? (toNumber(data?.width) ** 2 + toNumber(data?.height) ** 2) ** 0.5 / 2
        : isRevert
          ? toNumber(data?.height) / 2
          : toNumber(data?.width) / 2) +
      25 +
      (isIpad.value ? 18 : 0)
    const positionY = toNumber(data?.y) - toNumber(data?.height) / 2 - 50
    const checkpositionX = isMobile.value ? 800 : isIpad.value ? 950 : 1000
    containerAction.x = positionX > checkpositionX ? checkpositionX : positionX
    const checkpositionY = isIpad.value ? 200 : 150
    const lowercheckpositionY =
      focusedPhotoData.data && isIpad.value ? 400 : 480
    containerAction.y =
      positionY < checkpositionY
        ? checkpositionY
        : positionY > lowercheckpositionY && isIpad.value
          ? lowercheckpositionY
          : positionY

    trashSprite.y = 110
    let actionHeight = 181
    if (focusedStickerData.data || focusedPhotoData.data) {
      if (focusedStickerData.data) {
        arrowsMaximizeSprite.visible = false
        trashText.y = arrowsMaximizeSprite.y + arrowsMaximizeSprite.height - 6
        textSprite.visible = false
      } else if (focusedPhotoData.data) {
        trashSprite.y = 165
        actionHeight = 241
        trashText.y = trashSprite.y + 36
        arrowsMaximizeSprite.visible = true
        textSprite.visible = true
      }
    } else if (focusedTextData.data) {
      if (!textFocusedData.value) return
      arrowsMaximizeSprite.visible = false
      textSprite.visible = false
      trashText.y = arrowsMaximizeSprite.y + arrowsMaximizeSprite.height - 6
    }
    backgroundAction.roundRect(-12, -17, 56, actionHeight, 8).fill(0xffffff)
  } catch (error) {
    console.log(error)
  }
}

const onDragStickerStart = (
  event: FederatedPointerEvent,
  stickerSprite: TCustomSprite
) => {
  event.stopImmediatePropagation()
  handleClearFocus()
  selectedData.component = {
    x: stickerSprite.x,
    y: stickerSprite.y
  }
  selectedData.id = stickerSprite.id || ''
  selectedData.x = event.global.x
  selectedData.y = event.global.y
  selectedData.dragging = true
  selectedData.type = 'sticker'

  focusedStickerData.data = stickerSprite
  app.stage.hitArea = new PIXI.Rectangle(0, 0, maxWidth * 3, maxHeight * 2)
  app.stage.on('pointermove', (e) => onDragSelectedMove(e, stickerSprite))
}

const onDragPhotoStart = (
  event: FederatedPointerEvent,
  photoSprite: TCustomSprite
) => {
  event.stopImmediatePropagation()
  handleClearFocus()
  selectedData.component = {
    x: photoSprite.x,
    y: photoSprite.y
  }
  selectedData.id = photoSprite.id || ''
  selectedData.x = event.global.x
  selectedData.y = event.global.y
  selectedData.dragging = true
  selectedData.type = 'photo'

  focusedPhotoData.data = photoSprite
  app.stage.on('pointermove', (e) => onDragSelectedMove(e, photoSprite))
}

const handleUpdateBackgroundCaseColor = async () => {
  try {
    emits('changeButtonDisabled', true)
    backgroundColorCase.rect(0, 0, 1000, 1000)
    !(background_color.value?.hex || background_color.value?.cmyk?.c) &&
      backgroundColorCase.fill('#000000')
    backgroundColorCase.visible = true
    backgroundColorCase.alpha = 0
    backgroundCaseSprite.visible = false

    if (background_color.value?.image) {
      backgroundColorCase.visible = false
      backgroundCaseSprite.visible = true
      backgroundColorCase.alpha = 0
      backgroundCaseSprite.texture = await PIXI.Assets.load(
        config.public.mediaUrl + background_color.value?.image
      )
      backgroundCaseSprite.position.set(
        toNumber(maxWidth * 3) / 2,
        toNumber(maxHeight * 2) / 2
      )
      backgroundCaseSprite.scale.set(
        Math.max(
          toNumber(maxWidth) / backgroundCaseSprite.texture.width,
          toNumber(maxHeight) / backgroundCaseSprite.texture.height
        )
      )
      backgroundCaseSprite.anchor.set(0.5)
    } else if (background_color.value?.hex || background_color.value?.cmyk?.c) {
      backgroundCaseSprite.visible = false
      backgroundColorCase.visible = true
      backgroundColorCase.alpha = 1
      backgroundColorCase.fill(
        background_color.value?.cmyk?.c
          ? cmyk2rgb({
              c: toNumber(background_color.value?.cmyk?.c),
              m: toNumber(background_color.value?.cmyk?.m),
              y: toNumber(background_color.value?.cmyk?.y),
              k: toNumber(background_color.value?.cmyk?.k)
            })
          : background_color.value?.hex
      )
      backgroundCaseSprite.texture = await PIXI.Assets.load('')
    }
    emits('changeButtonDisabled', false)
  } catch (error) {
    console.log(error)
  }
}

// const { value: case_type } = useField<ICustomCase['case_type']>(
//   'case_type',
//   (inputValue) => !!inputValue
// )

const { value: background_color } = useField<ICustomCase['background_color']>(
  'background_color',
  (inputValue) => !!inputValue
)

const { value: stickers } = useField<ICustomCase['stickers']>(
  'stickers',
  (inputValue) => !!inputValue
)

const { value: texts } = useField<ICustomCase['texts']>(
  'texts',
  (inputValue) => !!inputValue
)

const { value: text_custom_change_count } = useField<
  ICustomCase['text_custom_change_count']
>('text_custom_change_count', (inputValue) => !!inputValue)

const { resetField: resetFieldFocusedText } = useField<
  ICustomCase['focused_text']
>('focused_text', (inputValue) => !!inputValue)

const { value: photos } = useField<ICustomCase['photos']>(
  'photos',
  (inputValue) => !!inputValue
)

const { value: deviceSelected } = useField<ICustomCase['device_selected']>(
  'device_selected',
  (inputValue) => !!inputValue
)

const { value: activeMenu } = useField<ICustomCase['active_menu']>(
  'active_menu',
  (inputValue) => !!inputValue
)

const handleChangeAppScale = () => {
  try {
    if (props.canvasHeight) {
      const canvas = document
        .getElementById(`pixi-app-${id}`)
        ?.getElementsByTagName('canvas')?.[0]
      const isIpad =
        width.value > 768 &&
        width.value < 1280 &&
        toNumber(window?.innerHeight) > 1000
      if (canvas)
        canvas.style.transform = `scale(${Math.max((props.canvasHeight + 30) / toNumber(window?.innerHeight), isIpad ? 1 : 0.5)})`
      containerAction.scale.set(
        Math.max((533 - 20 - 20) / props.canvasHeight, 1)
      )
    }
  } catch (error) {
    console.log(error)
  }
}

watch(stickers, (newValue, oldValue) =>
  handlePrepareStickerUi({ oldValue, newValue })
)
watch(() => texts.value, handlePrepareTextCustomUi)
watch(() => text_custom_change_count.value, handlePrepareTextCustomUi)
watch(photos, (newValue, oldValue) =>
  handlePreparePhotoUi({ oldValue, newValue })
)
watch(
  () => activeMenu.value,
  () =>
    activeMenu.value === 'text' &&
    !texts.value?.[0]?.id &&
    handleNewTextCustom()
)
watch(
  () => activeMenu.value,
  () => activeMenu.value !== 'text' && handleClearFocus()
)
watch(
  [
    () => deviceSelected.value?.case_image,
    () => deviceSelected.value?.device_image,
    () => deviceSelected.value?.mark_image
  ],
  () => handleUpdateDevice()
)
watch(() => background_color.value, handleUpdateBackgroundCaseColor, {
  deep: true
})

watch(() => props.canvasHeight, handleChangeAppScale)

// let time_spent: number = 0
// let timer: NodeJS.Timeout

// const handleBeforeUnload = () => {
//   clearTimeout(timer)
//   console.log('Fired', route.path)
//   route.path !== '/th/personalization-cases/print-preview' &&
//     !savedGalleryId.value &&
//     on('discard_customization', {
//       ...(user.value?.id && { user_id: user.value?.id }),
//       product_type: case_type.value.name,
//       time_spent_seconds: time_spent
//     })
// }

// onMounted(() => {
//   timer = setInterval(() => {
//     time_spent += 1
//   }, 1000)
//   window.addEventListener('beforeunload', handleBeforeUnload)
// })

// onUnmounted(() => {
//   clearTimeout(timer)
//   window.removeEventListener('beforeunload', handleBeforeUnload)
// })

watch(
  () => metadata.value?.textStyle,
  async (newValue) => {
    if (fontsLoadedList.value?.includes(newValue.name)) return
    PIXI.Assets.addBundle('fonts', [
      { alias: newValue?.name, src: config.public.mediaUrl + newValue?.url }
    ])
    await PIXI.Assets.loadBundle('fonts').catch((error) => console.log(error))
    fontsLoadedList.value = [...(fontsLoadedList.value || []), newValue?.name]
  },
  { deep: true }
)

watch(
  () => texts.value,
  async (newValue) => {
    for await (const value of newValue) {
      if (fontsLoadedList.value?.includes(value?.font_type)) return
      const url = find(
        props.templateMetadata?.text?.fontFamily,
        (item) => item.name === value?.font_type
      )?.font_file_url
      if (!url) return

      const fontUrl = config.public.mediaUrl + url

      PIXI.Assets.addBundle('fonts', [
        {
          alias: value?.font_type,
          src: fontUrl
        }
      ])
      await PIXI.Assets.loadBundle('fonts')
        .then((res) => {
          console.log('Font Loaded', res)
        })
        .catch((error) => console.log(error))
      fontsLoadedList.value = [
        ...(fontsLoadedList.value || []),
        value?.font_type
      ]
    }
  },
  { deep: true }
)

onUnmounted(() => {
  window.removeEventListener('keydown', onKeysDown)
  window.removeEventListener('keyup', onKeysUp)
})
</script>
